import { useEffect, useState } from "react";
import { collection, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { onSnapshotResilient } from "../utils/resilientSnapshot";
import type { Interaction } from "../types";

export interface InteractionWithId extends Interaction {
  id: string;
}

/**
 * The last list each trip's listener delivered, kept for the session. Coming
 * back to a page (Trips → Today) draws from it straight away instead of a
 * frame of "nothing logged" before the snapshot answers, which made Today
 * flash its trip pass before the live card.
 */
const lastSeen = new Map<string, InteractionWithId[]>();

export function useInteractions(tripId: string | undefined) {
  const [interactions, setInteractions] = useState<InteractionWithId[]>(
    () => (tripId ? lastSeen.get(tripId) : undefined) ?? [],
  );
  const [loading, setLoading] = useState(() => !(tripId && lastSeen.has(tripId)));

  useEffect(() => {
    if (!tripId) {
      setInteractions([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "interactions"),
      where("tripId", "==", tripId),
      orderBy("createdAt", "desc")
    );

    // Resilient: the rules resolve trip membership through the trip doc, so
    // this is denied until a freshly created trip reaches the server.
    const unsub = onSnapshotResilient(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as InteractionWithId[];
        lastSeen.set(tripId, data);
        setInteractions(data);
        setLoading(false);
      },
      { label: `interactions of trip ${tripId}`, onError: () => setLoading(false) },
    );

    return unsub;
  }, [tripId]);

  return { interactions, loading };
}
